import { useMemo } from 'react';
import { addDays, startOfDay } from 'date-fns';
import type {
  CashListTransactionFilters,
  CashListTransactionType,
} from '@pflegenavi/frontend/api-nursing-home';
import { useCashListConfiguration } from '@pflegenavi/frontend/api-nursing-home';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';
import type { UrlFilters } from '@pflegenavi/web-components';
import {
  urlDateRange,
  urlString,
  useUrlFilters,
} from '@pflegenavi/web-components';

export type CashListTransactionTab = 'all' | CashListTransactionType;

const TYPES: CashListTransactionType[] = [
  'deposit',
  'withdrawal',
  'adjustment',
  'stripe_transfer',
];

// `cashListId` keeps existing deep links to the page working.
const CODECS = {
  cashList: urlString('cashListId'),
  createdAt: urlDateRange('from', 'to'),
  employee: urlString('employee'),
  tab: urlString('tab'),
};

export type CashListTransactionFilterKey = keyof typeof CODECS;

export interface CashListTransactionFiltersState
  extends UrlFilters<typeof CODECS> {
  /** Falls back to the configured cash list: one is always selected. */
  cashListId: string | undefined;
  tab: CashListTransactionTab;
  filters: CashListTransactionFilters;
  cashLists: Array<{ id: string; name: string }>;
}

const isType = (value: string | undefined): value is CashListTransactionType =>
  TYPES.some((type) => type === value);

export function useCashListTransactionFilters(): CashListTransactionFiltersState {
  const urlFilters = useUrlFilters(CODECS);
  const { values } = urlFilters;
  const { cashLists, selectedCashListId } = useCashListConfiguration();

  const stripeTransferEnabled = useFeatureFlagEnabled(
    FeatureFlag.AccountingPayments
  );

  // The stripe transfer tab is hidden without the flag, so ignore it in deep links.
  const type =
    isType(values.tab) &&
    (values.tab !== 'stripe_transfer' || stripeTransferEnabled)
      ? values.tab
      : undefined;

  const filters = useMemo<CashListTransactionFilters>(
    () => ({
      type,
      employeeId: values.employee,
      createdFrom: values.createdAt?.from?.toISOString(),
      createdTo: values.createdAt?.to
        ? startOfDay(addDays(values.createdAt.to, 1)).toISOString()
        : undefined,
    }),
    [type, values.employee, values.createdAt]
  );

  const cashListId = values.cashList ?? selectedCashListId;

  return useMemo(
    () => ({
      ...urlFilters,
      cashListId,
      tab: type ?? 'all',
      filters,
      cashLists,
    }),
    [urlFilters, cashListId, type, filters, cashLists]
  );
}
